// Email Template Color Transformation Utilities
// Handles two-way transformation between actual colors and placeholder tokens

/**
 * CSS class to color placeholder mapping
 */
const COLOR_CLASS_MAPPINGS = {
    'tenancy-primary-bg': { property: 'background-color', placeholder: '{primaryColor}' },
    'tenancy-secondary-bg': { property: 'background-color', placeholder: '{secondaryColor}' },
    'tenancy-tertiary-bg': { property: 'background-color', placeholder: '{tertiaryColor}' },
    'tenancy-primary-text': { property: 'color', placeholder: '{primaryColor}' },
    'tenancy-secondary-text': { property: 'color', placeholder: '{secondaryColor}' },
    'tenancy-tertiary-text': { property: 'color', placeholder: '{tertiaryColor}' },
    'tenancy-primary-border': { property: 'border-color', placeholder: '{primaryColor}' },
    'tenancy-secondary-border': { property: 'border-color', placeholder: '{secondaryColor}' },
    'tenancy-tertiary-border': { property: 'border-color', placeholder: '{tertiaryColor}' },
};

/**
 * Transform template from placeholder tokens to actual colors for editing/preview
 * @param {string} templateHtml - HTML template with placeholder tokens
 * @param {object} colors - Object with actual color values { primary, secondary, tertiary }
 * @returns {string} HTML with actual color values applied
 */
export const transformTemplateForEditing = (templateHtml, colors) => {
    if (!templateHtml || !colors) {
        return templateHtml;
    }

    // Create a DOM parser to work with the HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(templateHtml, 'text/html');

    // Process each class mapping
    Object.entries(COLOR_CLASS_MAPPINGS).forEach(([className, config]) => {
        const elements = doc.querySelectorAll(`.${className}`);

        elements.forEach(element => {
            const style = element.getAttribute('style') || '';
            const colorValue = getColorValue(colors, config.placeholder);

            if (colorValue) {
                // Replace the placeholder with actual color value
                const updatedStyle = replaceStyleProperty(style, config.property, config.placeholder, colorValue);
                element.setAttribute('style', updatedStyle);
            }
        });
    });

    // Also handle direct placeholder replacements in style attributes
    const allElements = doc.querySelectorAll('*[style*="{tenancy."]');
    allElements.forEach(element => {
        let style = element.getAttribute('style');

        // Replace all color placeholders with actual values
        style = style.replace(/\{\primaryColor\}/g, colors.primary || '#000000');
        style = style.replace(/\{\secondaryColor\}/g, colors.secondary || '#666666');
        style = style.replace(/\{\tertiaryColor\}/g, colors.tertiary || '#cccccc');

        element.setAttribute('style', style);
    });

    return doc.documentElement.outerHTML;
};

/**
 * Transform template from actual colors back to placeholder tokens for saving
 * @param {string} templateHtml - HTML template with actual color values  
 * @param {object} colors - Object with actual color values { primary, secondary, tertiary }
 * @returns {string} HTML with placeholder tokens restored
 */
export const transformTemplateForSaving = (templateHtml, colors) => {
    if (!templateHtml || !colors) {
        return templateHtml;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(templateHtml, 'text/html');

    // Process each class mapping
    Object.entries(COLOR_CLASS_MAPPINGS).forEach(([className, config]) => {
        const elements = doc.querySelectorAll(`.${className}`);

        elements.forEach(element => {
            const style = element.getAttribute('style') || '';
            const colorValue = getColorValue(colors, config.placeholder);

            if (colorValue) {
                // Replace actual color value back to placeholder
                const updatedStyle = replaceStyleProperty(style, config.property, colorValue, config.placeholder);
                element.setAttribute('style', updatedStyle);
            }
        });
    });

    // Also handle direct color values in style attributes (reverse replacement)
    const allElements = doc.querySelectorAll('*[style]');
    allElements.forEach(element => {
        let style = element.getAttribute('style');

        // Replace actual color values back to placeholders
        if (colors.primary) {
            style = style.replace(new RegExp(escapeRegExp(colors.primary), 'gi'), '{primaryColor}');
        }
        if (colors.secondary) {
            style = style.replace(new RegExp(escapeRegExp(colors.secondary), 'gi'), '{secondaryColor}');
        }
        if (colors.tertiary) {
            style = style.replace(new RegExp(escapeRegExp(colors.tertiary), 'gi'), '{tertiaryColor}');
        }

        element.setAttribute('style', style);
    });

    return doc.documentElement.outerHTML;
};

/**
 * Get color value from colors object based on placeholder
 * @param {object} colors - Colors object
 * @param {string} placeholder - Placeholder token
 * @returns {string|null} Color value or null
 */
const getColorValue = (colors, placeholder) => {
    switch (placeholder) {
        case '{primaryColor}':
            return colors.primary;
        case '{secondaryColor}':
            return colors.secondary;
        case '{tertiaryColor}':
            return colors.tertiary;
        default:
            return null;
    }
};

/**
 * Replace a specific CSS property value in a style string
 * @param {string} style - CSS style string
 * @param {string} property - CSS property name
 * @param {string} oldValue - Value to replace
 * @param {string} newValue - New value
 * @returns {string} Updated style string
 */
const replaceStyleProperty = (style, property, oldValue, newValue) => {
    // Handle both with and without semicolon endings
    const patterns = [
        new RegExp(`(${property}\\s*:\\s*)${escapeRegExp(oldValue)}(\\s*;?)`, 'gi'),
        new RegExp(`(${property}\\s*:\\s*)${escapeRegExp(oldValue)}(\\s*$)`, 'gi')
    ];

    let updatedStyle = style;
    patterns.forEach(pattern => {
        updatedStyle = updatedStyle.replace(pattern, `$1${newValue}$2`);
    });

    return updatedStyle;
};

/**
 * Escape special regex characters
 * @param {string} string - String to escape
 * @returns {string} Escaped string
 */
const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

/**
 * Transform logo placeholder to actual URL for editing/preview
 * @param {string} templateHtml - HTML template
 * @param {string} logoUrl - Actual logo URL
 * @returns {string} HTML with logo URL applied
 */
export const transformLogoForEditing = (templateHtml, logoUrl) => {
    if (!templateHtml) return templateHtml;

    const logoToUse = logoUrl || getDefaultLogoDataUrl();
    return templateHtml.replace(/\{client\.logo\}/g, logoToUse);
};

/**
 * Transform actual logo URL back to placeholder for saving
 * @param {string} templateHtml - HTML template
 * @param {string} logoUrl - Actual logo URL that was used
 * @returns {string} HTML with logo placeholder restored
 */
export const transformLogoForSaving = (templateHtml, logoUrl) => {
    if (!templateHtml) return templateHtml;

    let transformed = templateHtml;

    // Replace actual logo URL if it exists (full URL)
    if (logoUrl) {
        transformed = transformed.replace(new RegExp(escapeRegExp(logoUrl), 'g'), '{client.logo}');
    }

    // CRITICAL FIX: Replace relative paths that match the logo filename
    if (logoUrl) {
        // Extract just the filename from the full URL
        const logoFilename = logoUrl.split('/').pop();
        if (logoFilename) {
            // Replace any relative path that contains this filename
            const relativePathPattern = new RegExp(`assets/logos/${escapeRegExp(logoFilename)}`, 'g');
            transformed = transformed.replace(relativePathPattern, '{client.logo}');

            // Also handle just the filename without path
            const filenamePattern = new RegExp(`src="${escapeRegExp(logoFilename)}"`, 'g');
            transformed = transformed.replace(filenamePattern, 'src="{client.logo}"');
        }
    }

    // FIX FOR TINYMCE: Remove ../ prefix that TinyMCE adds to placeholders
    transformed = transformed.replace(/src="\.\.\/\{client\.logo\}"/g, 'src="{client.logo}"');

    // Also handle any other relative prefixes TinyMCE might add
    transformed = transformed.replace(/src="[\.\/]*\{client\.logo\}"/g, 'src="{client.logo}"');

    // Always replace the default "No Logo" data URL with placeholder
    const defaultLogoDataUrl = getDefaultLogoDataUrl();
    transformed = transformed.replace(new RegExp(escapeRegExp(defaultLogoDataUrl), 'g'), '{client.logo}');

    // Handle base64 versions that TinyMCE might create
    const base64Pattern = /data:image\/svg\+xml;base64,[A-Za-z0-9+/=]*[^"]*(?="[^>]*alt="Logo")/g;
    transformed = transformed.replace(base64Pattern, '{client.logo}');

    // ADDITIONAL FIX: Catch any remaining relative logo paths
    const genericRelativePattern = /src="assets\/logos\/[^"]*"/g;
    transformed = transformed.replace(genericRelativePattern, 'src="{client.logo}"');

    return transformed;
};

/**
 * Get default logo data URL for when no logo is available
 * @returns {string} Data URL for placeholder logo
 */
const getDefaultLogoDataUrl = () => {
    return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='40' viewBox='0 0 140 40'%3E%3Crect width='140' height='40' fill='%23f0f0f0' stroke='%23ddd'/%3E%3Ctext x='70' y='20' font-family='Arial' font-size='12' text-anchor='middle' dominant-baseline='middle' fill='%23999'%3ENo Logo%3C/text%3E%3C/svg%3E`;
};

/**
 * Apply all transformations for editing (colors + logo)
 * @param {string} templateHtml - HTML template
 * @param {object} colors - Color values
 * @param {string} logoUrl - Logo URL
 * @returns {string} Transformed HTML
 */
export const applyAllTransformationsForEditing = (templateHtml, colors, logoUrl) => {
    let transformed = transformTemplateForEditing(templateHtml, colors);
    transformed = transformLogoForEditing(transformed, logoUrl);
    return transformed;
};

/**
 * Apply all transformations for saving (colors + logo)
 * @param {string} templateHtml - HTML template
 * @param {object} colors - Color values
 * @param {string} logoUrl - Logo URL
 * @returns {string} Transformed HTML
 */
export const applyAllTransformationsForSaving = (templateHtml, colors, logoUrl) => {
    let transformed = transformTemplateForSaving(templateHtml, colors);
    transformed = transformLogoForSaving(transformed, logoUrl);
    return transformed;
};